import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  updateClosedLoan,
  type ProposeClosedLoanDirective,
  type ClosedLoanUpdateBody,
} from "../../api";

interface Props {
  directive: ProposeClosedLoanDirective;
  onSaved: (dealName: string) => void;
  onCancel: () => void;
}

type FieldKey =
  | "final_loan_amount"
  | "final_rate"
  | "final_term_months"
  | "position"
  | "payment_amount"
  | "close_date"
  | "loan_due_date"
  | "commission_amount";

type FieldType = "money" | "pct" | "int" | "text" | "date";
type FieldState = "empty" | "new" | "current_only" | "match" | "conflict";

const ROWS: Array<{ key: FieldKey; label: string; type: FieldType }> = [
  { key: "final_loan_amount", label: "Final Loan Amount", type: "money" },
  { key: "final_rate", label: "Final Rate (%)", type: "pct" },
  { key: "final_term_months", label: "Final Term (months)", type: "int" },
  { key: "position", label: "Final Position", type: "text" },
  { key: "payment_amount", label: "Payment Amount", type: "money" },
  { key: "close_date", label: "Close Date", type: "date" },
  { key: "loan_due_date", label: "Loan Due Date", type: "date" },
  { key: "commission_amount", label: "Commission Amount", type: "money" },
];

// ── Format-tolerant equality ────────────────────────────────────────────────
function parseNum(s: string): number | null {
  const cleaned = s.replace(/[$,%\s]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}
/** Numbers/rates: compare rounded to 2dp (so 12.99 == 12.990, 1000 == 1000.00). */
function numEq(a: string, b: string): boolean {
  const na = parseNum(a);
  const nb = parseNum(b);
  if (na == null || nb == null) return na === nb;
  return Math.round(na * 100) === Math.round(nb * 100);
}
/** Normalize a date string ("2026-05-28" or "May 28, 2026") to YYYY-MM-DD. */
function toISO(s: string): string | null {
  const t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const d = new Date(t);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}
function dateEq(a: string, b: string): boolean {
  const ia = toISO(a);
  const ib = toISO(b);
  return ia != null && ib != null && ia === ib;
}
function strEq(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
function eqByType(type: FieldType, a: string, b: string): boolean {
  if (type === "date") return dateEq(a, b);
  if (type === "text") return strEq(a, b);
  return numEq(a, b); // money | pct | int
}

// ── Display formatting (for the Currently/Extracted compare lines) ──────────
function fmtDisplay(type: FieldType, raw: string): string {
  if (!raw) return "—";
  if (type === "money") {
    const n = parseNum(raw);
    return n == null
      ? raw
      : new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }).format(n);
  }
  if (type === "pct") {
    const n = parseNum(raw);
    return n == null ? raw : `${n}%`;
  }
  if (type === "date") {
    const iso = toISO(raw);
    if (!iso) return raw;
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(`${iso}T12:00:00Z`));
  }
  return raw;
}

function confLabel(c: number | undefined): { text: string; cls: string } | null {
  if (c == null) return null;
  if (c >= 0.8) return { text: `high ${Math.round(c * 100)}%`, cls: "conf-high" };
  if (c >= 0.5) return { text: `med ${Math.round(c * 100)}%`, cls: "conf-med" };
  return { text: `low ${Math.round(c * 100)}%`, cls: "conf-low" };
}

interface FieldModel {
  key: FieldKey;
  label: string;
  type: FieldType;
  state: FieldState;
  extractedStr: string; // normalized (dates → ISO)
  currentStr: string;
  conf?: number;
  source?: string;
}

/**
 * ClosedLoanReviewModal — review + confirm AI-extracted closing details before
 * writing to Zoho. FAIL-SAFE: a field already populated in Zoho is never
 * silently overwritten. Per field we classify:
 *   empty        — no value either side → no write
 *   new          — Zoho empty, extracted present → default to extracted
 *   current_only — Zoho has a value, nothing extracted → keep current
 *   match        — both agree (format-tolerant) → confirmed
 *   conflict     — both present and differ → amber row, default KEEP CURRENT,
 *                  user must explicitly switch to USE EXTRACTED or type a value
 * A banner counts conflicts; a confirm strip summarizes new/changed/unchanged
 * before the actual save. Only new + changed fields are written.
 */
export function ClosedLoanReviewModal({ directive, onSaved, onCancel }: Props) {
  const { fields, current, lender, lender_candidates } = directive;

  const models: FieldModel[] = useMemo(() => {
    return ROWS.map(({ key, label, type }) => {
      const exRaw = fields[key]?.value;
      const curRaw = current[key];
      const hasEx = exRaw != null && String(exRaw).trim() !== "";
      const hasCur = curRaw != null && String(curRaw).trim() !== "";
      let extractedStr = hasEx ? String(exRaw) : "";
      if (type === "date" && extractedStr) extractedStr = toISO(extractedStr) ?? extractedStr;
      let currentStr = hasCur ? String(curRaw) : "";
      if (type === "date" && currentStr) currentStr = toISO(currentStr) ?? currentStr;

      let state: FieldState;
      if (!hasEx && !hasCur) state = "empty";
      else if (hasEx && !hasCur) state = "new";
      else if (!hasEx && hasCur) state = "current_only";
      else state = eqByType(type, extractedStr, currentStr) ? "match" : "conflict";

      return {
        key,
        label,
        type,
        state,
        extractedStr,
        currentStr,
        conf: fields[key]?.confidence,
        source: fields[key]?.source,
      };
    });
  }, [fields, current]);

  // Seed each input: new→extracted, conflict/match/current_only→current (safe), empty→"".
  const initial = useMemo(() => {
    const seed = {} as Record<FieldKey, string>;
    for (const m of models) {
      seed[m.key] =
        m.state === "new" ? m.extractedStr : m.state === "empty" ? "" : m.currentStr;
    }
    return seed;
  }, [models]);

  const [vals, setVals] = useState<Record<FieldKey, string>>(initial);
  useEffect(() => setVals(initial), [initial]);
  const setField = (k: FieldKey, v: string) => setVals((p) => ({ ...p, [k]: v }));

  // ── Lender (compare by id) ──
  const exLenderId = lender?.matched_id ?? null;
  const exLenderName = lender?.matched_name ?? lender?.extracted_name ?? null;
  const curLenderId = current.lender_id;
  const curLenderName = current.lender_name;
  const hasExLender = Boolean(lender && (lender.matched_id || lender.extracted_name));
  const hasCurLender = Boolean(curLenderId);
  const lenderState: FieldState = !hasExLender && !hasCurLender
    ? "empty"
    : hasExLender && !hasCurLender
      ? "new"
      : !hasExLender && hasCurLender
        ? "current_only"
        : exLenderId && curLenderId && exLenderId === curLenderId
          ? "match"
          : "conflict";

  const [lenderId, setLenderId] = useState<string>(
    lenderState === "new" ? exLenderId ?? "" : curLenderId ?? "",
  );

  const lenderOptions = useMemo(() => {
    const m = new Map<string, string>();
    if (curLenderId && curLenderName) m.set(curLenderId, curLenderName);
    for (const c of lender_candidates) m.set(c.id, c.name);
    if (exLenderId && exLenderName && !m.has(exLenderId)) m.set(exLenderId, exLenderName);
    return [...m].map(([id, name]) => ({ id, name }));
  }, [curLenderId, curLenderName, exLenderId, exLenderName, lender_candidates]);

  const [referral, setReferral] = useState<string>(
    current.referral_fee_paid != null ? String(current.referral_fee_paid) : "",
  );

  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const conflictCount =
    models.filter((m) => m.state === "conflict").length +
    (lenderState === "conflict" ? 1 : 0);

  // ── Save classification: new fills vs changes vs unchanged ──
  const summary = useMemo(() => {
    let newCount = 0;
    let changeCount = 0;
    let unchangedCount = 0;
    for (const m of models) {
      const v = (vals[m.key] ?? "").trim();
      if (!v && !m.currentStr) continue; // nothing either side
      if (!m.currentStr && v) { newCount++; continue; }
      if (m.currentStr && !v) { unchangedCount++; continue; } // won't clear
      if (eqByType(m.type, v, m.currentStr)) unchangedCount++;
      else changeCount++;
    }
    if (lenderId && !curLenderId) newCount++;
    else if (lenderId && curLenderId && lenderId !== curLenderId) changeCount++;
    else if (lenderId && curLenderId) unchangedCount++;
    const rv = referral.trim();
    if (rv) {
      if (current.referral_fee_paid == null) newCount++;
      else if (!numEq(rv, String(current.referral_fee_paid))) changeCount++;
      else unchangedCount++;
    }
    return { newCount, changeCount, unchangedCount };
  }, [models, vals, lenderId, curLenderId, referral, current.referral_fee_paid]);

  const nothingToWrite = summary.newCount + summary.changeCount === 0;

  function buildBody(): ClosedLoanUpdateBody {
    // Accumulate loosely — m.key is a union of keys with differing value types,
    // which TS won't let us index-assign directly; cast at the end.
    const body: Record<string, string | number | null> = {};
    for (const m of models) {
      const v = (vals[m.key] ?? "").trim();
      if (!v) continue; // never write null/empty over an existing value
      if (m.currentStr && eqByType(m.type, v, m.currentStr)) continue; // unchanged
      if (m.type === "text") body[m.key] = v;
      else if (m.type === "date") body[m.key] = toISO(v) ?? v;
      else body[m.key] = parseNum(v); // money | pct | int
    }
    if (lenderId && lenderId !== (curLenderId ?? "")) body.lender_id = lenderId;
    const rv = referral.trim();
    if (rv && !(current.referral_fee_paid != null && numEq(rv, String(current.referral_fee_paid)))) {
      body.referral_fee_paid = parseNum(rv);
    }
    return body as ClosedLoanUpdateBody;
  }

  const finalCommissionPreview = useMemo(() => {
    const comm = parseNum(vals.commission_amount ?? "");
    const ref = parseNum(referral) ?? 0;
    return comm == null ? null : comm - ref;
  }, [vals.commission_amount, referral]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await updateClosedLoan(directive.deal_id, buildBody());
      onSaved(directive.deal_name);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "save_failed",
      );
      setSaving(false);
    }
  }

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card closed-loan-modal-card">
        <div className="write-confirm-title">
          CLOSED LOAN DETAILS — {directive.deal_name}
        </div>

        {conflictCount > 0 && (
          <div className="cl-conflict-banner">
            ⚠ {conflictCount} field{conflictCount === 1 ? "" : "s"} differ from
            current Zoho values. Each defaults to <em>keep current</em> — review
            and switch to the extracted value only where it’s correct.
          </div>
        )}
        <div className="closed-loan-modal-sub">
          Review the extracted values, resolve any conflicts, then save to Zoho.
        </div>

        <div className="closed-loan-modal-rows">
          {models.map((m) => (
            <FieldRow
              key={m.key}
              model={m}
              value={vals[m.key] ?? ""}
              onChange={(v) => setField(m.key, v)}
            />
          ))}

          {/* Lender — picker over current + resolved candidates */}
          <div className={`cl-row${lenderState === "conflict" ? " cl-row-conflict" : ""}`}>
            <div className="cl-row-head">
              <span className="cl-row-label">Lender</span>
              {lenderState === "match" && <span className="cl-match-ok">✓ confirmed</span>}
              {lender?.extracted_name && (
                <span className="cl-src">doc: “{lender.extracted_name}”</span>
              )}
            </div>
            {lenderState === "conflict" && (
              <div className="cl-compare">
                <div className="cl-compare-line">
                  <span className="cl-compare-tag">Currently</span>
                  {curLenderName ?? "—"}
                </div>
                <div className="cl-compare-line">
                  <span className="cl-compare-tag cl-compare-tag-ex">Extracted</span>
                  {exLenderName ?? "—"}
                </div>
              </div>
            )}
            <select
              className="cl-input"
              value={lenderId}
              onChange={(e) => setLenderId(e.target.value)}
            >
              <option value="">— no change{curLenderName ? ` (keep ${curLenderName})` : ""} —</option>
              {lenderOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                  {o.id === curLenderId ? " (current)" : ""}
                  {o.id === exLenderId ? " (extracted)" : ""}
                </option>
              ))}
            </select>
            {hasExLender && exLenderId == null && (
              <div className="cl-warn">
                ⚠ Couldn’t auto-match “{lender?.extracted_name}” — pick manually.
              </div>
            )}
          </div>

          {/* Referral Fee Paid — manual only */}
          <div className="cl-row">
            <div className="cl-row-head">
              <span className="cl-row-label">Referral Fee Paid</span>
              <span className="cl-src">manual entry</span>
            </div>
            <input
              className="cl-input"
              type="text"
              value={referral}
              placeholder="$0.00"
              onChange={(e) => setReferral(e.target.value)}
            />
          </div>

          {/* Final Commission — computed preview, read-only */}
          <div className="cl-row cl-row-readonly">
            <div className="cl-row-head">
              <span className="cl-row-label">Final Commission</span>
              <span className="cl-src">auto-calculated by Zoho</span>
            </div>
            <div className="cl-readonly-value">
              {finalCommissionPreview == null
                ? "—"
                : new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 2,
                  }).format(finalCommissionPreview)}
              <span className="cl-readonly-note"> · Zoho recalculates on save</span>
            </div>
          </div>
        </div>

        {error && <div className="write-confirm-error">⚠ {error}</div>}

        {!confirming ? (
          <div className="closed-loan-modal-actions">
            <button className="write-confirm-cancel" onClick={onCancel}>
              CANCEL
            </button>
            <button
              className="write-confirm-confirm"
              onClick={() => setConfirming(true)}
              disabled={nothingToWrite}
              title={nothingToWrite ? "No new or changed values to save" : undefined}
            >
              {nothingToWrite ? "NOTHING TO SAVE" : "SAVE TO ZOHO"}
            </button>
          </div>
        ) : (
          <div className="cl-confirm-strip">
            <div className="cl-confirm-summary">
              Saving {summary.newCount} new {summary.newCount === 1 ? "value" : "values"},{" "}
              {summary.changeCount} change{summary.changeCount === 1 ? "" : "s"},{" "}
              {summary.unchangedCount} unchanged.
              {summary.changeCount > 0 && (
                <strong className="cl-confirm-changes">
                  {" "}
                  {summary.changeCount} existing value
                  {summary.changeCount === 1 ? "" : "s"} will be overwritten.
                </strong>
              )}
            </div>
            <div className="cl-confirm-actions">
              <button
                className="write-confirm-cancel"
                onClick={() => setConfirming(false)}
                disabled={saving}
              >
                CANCEL
              </button>
              <button
                className="write-confirm-confirm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "SAVING…" : "✓ CONFIRM SAVE"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Per-field row, rendered by state ────────────────────────────────────────
function FieldRow({
  model: m,
  value,
  onChange,
}: {
  model: FieldModel;
  value: string;
  onChange: (v: string) => void;
}) {
  const conf = confLabel(m.conf);
  const inputType = m.type === "date" ? "date" : "text";

  if (m.state === "empty") {
    return (
      <div className="cl-row cl-row-muted">
        <div className="cl-row-head">
          <span className="cl-row-label">{m.label}</span>
        </div>
        <div className="cl-muted-note">no value extracted, currently empty</div>
      </div>
    );
  }

  if (m.state === "conflict") {
    const isKeep = eqByType(m.type, value, m.currentStr);
    const isUse = eqByType(m.type, value, m.extractedStr);
    return (
      <div className="cl-row cl-row-conflict">
        <div className="cl-row-head">
          <span className="cl-row-label">{m.label}</span>
          {conf && <span className={`cl-conf ${conf.cls}`}>{conf.text}</span>}
          {m.source && <span className="cl-src">from {m.source}</span>}
        </div>
        <div className="cl-compare">
          <div className="cl-compare-line">
            <span className="cl-compare-tag">Currently</span>
            {fmtDisplay(m.type, m.currentStr)}
          </div>
          <div className="cl-compare-line">
            <span className="cl-compare-tag cl-compare-tag-ex">Extracted</span>
            {fmtDisplay(m.type, m.extractedStr)}
          </div>
        </div>
        <div className="cl-seg">
          <button
            className={`cl-seg-btn${isKeep ? " active" : ""}`}
            onClick={() => onChange(m.currentStr)}
          >
            KEEP CURRENT
          </button>
          <button
            className={`cl-seg-btn${isUse && !isKeep ? " active" : ""}`}
            onClick={() => onChange(m.extractedStr)}
          >
            USE EXTRACTED
          </button>
        </div>
        <input
          className="cl-input"
          type={inputType}
          value={value}
          placeholder="or type a different value"
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  // new | match | current_only — single editable input with a small status note
  return (
    <div className="cl-row">
      <div className="cl-row-head">
        <span className="cl-row-label">{m.label}</span>
        {conf && <span className={`cl-conf ${conf.cls}`}>{conf.text}</span>}
        {m.state === "new" && <span className="cl-new-tag">new</span>}
        {m.state === "match" && <span className="cl-match-ok">✓ confirmed</span>}
        {m.source && m.state !== "current_only" && (
          <span className="cl-src">from {m.source}</span>
        )}
        {m.state === "current_only" && <span className="cl-src">no value extracted</span>}
      </div>
      <input
        className="cl-input"
        type={inputType}
        value={value}
        placeholder={m.type === "date" ? "YYYY-MM-DD" : ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
